/**
 * Deroule d'une nuit, avec arbitrage des interruptions.
 *
 * C'est la fonction centrale de l'application, pas un ecran de reglage. Une
 * barre continue de 23 h 55 a 07 h 00 mentirait sur ce qui a ete mesure : le
 * telephone a servi a 3 h 12, et seul l'utilisateur sait s'il s'est rendormi
 * ou s'il a passe la nuit debout.
 *
 * Chaque bascule recalcule les grandeurs immediatement. Rien n'est demande en
 * plus d'un geste : au reveil, la patience est courte.
 */

import { mesurerNuit } from '../analysis/nuits'
import { epoch, formatDuree, formatHeure } from '../domain/time'
import { TYPE_EVENEMENT_LABELS, type Candidat, type Nuit } from '../domain/types'

/** Bornes successives du deroule : repos, usage, repos, usage, repos... */
interface Tranche {
  genre: 'repos' | 'usage'
  debut: string
  fin: string
  /** Index dans `nuit.interruptions`, pour les tranches d'usage. */
  index?: number
}

export function trancher(nuit: Nuit): Tranche[] {
  const tranches: Tranche[] = []
  let curseur = nuit.debut

  nuit.interruptions.forEach((i, index) => {
    if (epoch(i.debut) > epoch(curseur)) {
      tranches.push({ genre: 'repos', debut: curseur, fin: i.debut })
    }
    tranches.push({ genre: 'usage', debut: i.debut, fin: i.fin, index })
    curseur = i.fin
  })

  if (epoch(nuit.fin) > epoch(curseur)) {
    tranches.push({ genre: 'repos', debut: curseur, fin: nuit.fin })
  }
  return tranches
}

export function DerouleNuit({
  nuit,
  onBasculer,
  onSupprimerInterruption,
}: {
  nuit: Nuit
  onBasculer: (index: number) => void
  onSupprimerInterruption?: (index: number) => void
}) {
  const tranches = trancher(nuit)

  return (
    <div className="deroule">
      <div className="deroule__borne">
        <span className="deroule__pastille" />
        <span>{formatHeure(nuit.debut)}</span>
        <span className="tres-doux">posé · {TYPE_EVENEMENT_LABELS[nuit.origineDebut]}</span>
      </div>

      {tranches.map((t, k) => {
        if (t.genre === 'repos') {
          return (
            <div key={`r${k}`} className="deroule__segment">
              {formatDuree(epoch(t.fin) - epoch(t.debut))} sans usage
            </div>
          )
        }

        const index = t.index as number
        const interruption = nuit.interruptions[index]
        if (!interruption) return null
        const duree = epoch(t.fin) - epoch(t.debut)

        return (
          <div
            key={`u${k}`}
            className={interruption.ignoree ? 'interruption interruption--ignoree' : 'interruption'}
          >
            <div className="interruption__corps">
              <div className="interruption__titre">
                {formatHeure(t.debut)}
                {!interruption.dureeInconnue && duree > 0 && ` → ${formatHeure(t.fin)}`}
                <span className="tres-doux" style={{ fontWeight: 500 }}>
                  {interruption.dureeInconnue
                    ? ' · durée inconnue'
                    : ` · ${formatDuree(duree)} d’usage`}
                </span>
              </div>

              {interruption.dureeInconnue && (
                <p className="tres-doux">
                  Aucune ouverture d’app n’a été captée : on sait jusqu’à quand le téléphone a
                  servi, pas depuis quand.
                </p>
              )}

              <div className="ligne ligne--enroulee">
                <div className="bascule" role="group" aria-label="Compter cette interruption">
                  <button
                    type="button"
                    aria-pressed={!interruption.ignoree}
                    onClick={() => interruption.ignoree && onBasculer(index)}
                  >
                    Réveil
                  </button>
                  <button
                    type="button"
                    aria-pressed={interruption.ignoree}
                    onClick={() => !interruption.ignoree && onBasculer(index)}
                  >
                    Ignorer
                  </button>
                </div>
                {onSupprimerInterruption && (
                  <button
                    type="button"
                    className="btn btn--fantome btn--petit"
                    onClick={() => onSupprimerInterruption(index)}
                  >
                    Retirer
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })}

      <div className="deroule__borne">
        <span className="deroule__pastille" />
        <span>{formatHeure(nuit.fin)}</span>
        <span className="tres-doux">levé · {TYPE_EVENEMENT_LABELS[nuit.origineFin]}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

export function MesuresNuit({ nuit, objectifMinutes }: { nuit: Nuit; objectifMinutes: number }) {
  const m = mesurerNuit(nuit)
  const ecart = m.tempsPoseMs - objectifMinutes * 60_000

  return (
    <div className="pile">
      <div className="vedette">
        <span className="vedette__valeur">{formatDuree(m.tempsPoseMs)}</span>
        <span className="vedette__unite">
          de téléphone posé
          {/* On ne dit jamais « de sommeil » : ce n'est pas ce qui est mesure. */}
        </span>
      </div>

      <div className="mesures">
        <div className="mesure">
          <div className="mesure__valeur">{formatDuree(m.amplitudeMs)}</div>
          <div className="mesure__libelle">amplitude</div>
        </div>
        <div className="mesure">
          <div className="mesure__valeur">{m.reveils}</div>
          <div className="mesure__libelle">
            {m.reveils > 1 ? 'réveils décomptés' : 'réveil décompté'}
          </div>
        </div>
        <div className="mesure">
          <div className="mesure__valeur">{Math.round(m.efficacite * 100)} %</div>
          <div className="mesure__libelle">efficacité</div>
        </div>
      </div>

      <div className="jauge" aria-hidden="true">
        <div
          className="jauge__part"
          style={{ width: `${Math.min(100, (m.tempsPoseMs / (objectifMinutes * 60_000)) * 100)}%` }}
        />
      </div>
      <p className="tres-doux">
        Objectif {formatDuree(objectifMinutes * 60_000)} ·{' '}
        {ecart >= 0
          ? `atteint, ${formatDuree(ecart)} de plus`
          : `${formatDuree(-ecart)} de moins`}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------

/**
 * Choix d'une borne parmi les candidats conservees a la derivation.
 *
 * Sans cette liste, une soiree passee sur une app non surveillee — ou le
 * dernier signal capte est trop precoce — resterait fausse sans recours. Les
 * candidats sont stockes sur la nuit precisement pour rendre ce rattrapage
 * possible sans relire le journal d'evenements.
 */
export function ChoixCandidat({
  titre,
  candidats,
  retenu,
  onChoisir,
}: {
  titre: string
  candidats: Candidat[]
  retenu: string
  onChoisir: (c: Candidat) => void
}) {
  if (candidats.length < 2) return null

  return (
    <div className="pile pile--serree">
      <h3>{titre}</h3>
      <div className="candidats">
        {candidats.map((c) => (
          <button
            key={`${c.type}-${c.at}`}
            type="button"
            className="candidat"
            aria-pressed={c.at === retenu}
            onClick={() => onChoisir(c)}
          >
            <span className="candidat__heure">{formatHeure(c.at)}</span>
            <span className="grandir doux">{TYPE_EVENEMENT_LABELS[c.type]}</span>
            {c.at === retenu && <span className="puce puce--info">retenu</span>}
          </button>
        ))}
      </div>
    </div>
  )
}
